// --- React & Redux
import React, { useMemo } from "react";
// --- External UI Libraries
import Input from "@dtci/spellbook/Input";
import Pillbox from "@dtci/spellbook/Pillbox";
// --- Components
import { InputLabel } from "tap/components/InputLabel";
// --- Styles
import "./styles.scss";

/** Map of field names to display labels for filter pillboxes */
const FILTER_FIELD_LABELS = {
  tapOfferStatus: "TAP Offer Status",
  historyCreatedBy: "Modified By",
  needsAudit: "Needs Audit",
  readyToPublish: "Ready to Publish",
  offerStatus: "RL Offer Status",
  media: "Media",
  territory: "Territory",
  format: "Format",
  rating: "Rating",
  tier: "Tier",
  currency: "Currency",
  pmxPlatform: "PMX Platform",
};

/**
 * Offer History Search Form Component
 *
 * Provides search controls for the Offer History grid:
 * - Search All Fields - text search across all history fields
 * - Active filter pillboxes - shows selected filters with ability to remove
 *
 * Grid updates automatically as the user types.
 */
export function OfferHistorySearchForm({
  searchAll,
  onSearchChange,
  internalFilters = {},
  filterColumns = [],
  onRemoveFilter,
}) {
  /**
   * Build pillbox data from active filters
   */
  const filterPillboxes = useMemo(() => {
    const pillboxes = [];

    Object.entries(internalFilters).forEach(([field, values]) => {
      if (!values || values.length === 0) return;

      // Find the column label for this field (use filterColumns if available, fall back to static map)
      const column = filterColumns.find((col) => col.field === field);
      const fieldLabel = column?.label || FILTER_FIELD_LABELS[field] || field;

      values.forEach((option) => {
        pillboxes.push({
          field,
          value: option.value,
          fieldLabel,
          valueLabel: option.label,
        });
      });
    });

    return pillboxes;
  }, [internalFilters, filterColumns]);

  return (
    <div className="offer-history-filter-form">
      <div className="offer-history-filter-row">
        <div className="offer-history-search-input">
          <InputLabel colProps={{ span: 24 }} label="Search All Fields">
            <Input
              value={searchAll || ""}
              onChange={(e) => onSearchChange(e.target.value)}
              width="large"
              id="searchAll"
              name="searchAll"
              placeholder="Search..."
            />
          </InputLabel>
        </div>
        {filterPillboxes.length > 0 && (
          <div className="offer-history-filter-pillboxes">
            {filterPillboxes.map((pill) => (
              <Pillbox
                key={`${pill.field}-${pill.value}`}
                size="small"
                color="primary"
                light
                showClose
                onClose={() => onRemoveFilter(pill.field, pill.value)}
              >
                <strong>{pill.fieldLabel}:</strong> {pill.valueLabel}
              </Pillbox>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
